import { useEffect, useState } from 'react';
import { type Nullable } from 'corgi-types';

import { executeQuery } from 'common/gql';
import type {
  SideNavigationQuery,
  SideNavigationQueryVariables,
} from 'common/gql/generated/schema';
import { useGQLLocale } from 'common/hooks/locale';
import type { GraphqlNavigationItemProps } from 'common/types/NavigationItem';
import { captureException } from 'common/utils/error-reporter';
import { SideNavType } from 'common/utils/navigation';
import sideNavigationQuery from '../../gql/query/side-navigation.gql';
import type { ContentNavigationTab } from '../types';
import { sortNavigation } from '../utils';

interface UseFetchNavigationParams {
  initialNavigation?: Nullable<GraphqlNavigationItemProps>;
  isOpen: boolean;
  type: SideNavType;
  withSideNavTabs: boolean;
}

const DOCUMENT_ID = 'global-navigation-content-tree';

const fetchNavigation = (variables: SideNavigationQueryVariables) =>
  executeQuery<SideNavigationQuery, SideNavigationQueryVariables>({
    query: sideNavigationQuery,
    variables,
  });

export const useFetchNavigation = ({
  initialNavigation,
  isOpen,
  type,
  withSideNavTabs,
}: UseFetchNavigationParams) => {
  const isShopInShop = type === SideNavType.ShopInShop;

  const [navigation, setNavigation] = useState(initialNavigation);
  const [tabs, setTabs] = useState<ContentNavigationTab[]>(
    withSideNavTabs
      ? ((initialNavigation?.tabs?.filter(Boolean) as ContentNavigationTab[]) ?? [])
      : []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isFetched, setIsFetched] = useState(isShopInShop);
  const locale = useGQLLocale();

  useEffect(() => {
    const loadNavigationData = async () => {
      if (isShopInShop) {
        return;
      }

      if (isOpen && !isFetched) {
        setIsLoading(true);
        try {
          const response = await fetchNavigation({
            locale,
            uid: DOCUMENT_ID,
            withSideNavTabs,
          });
          const sideNav = response.content.globalNavigation.sideNav;
          setNavigation(sideNav as GraphqlNavigationItemProps);

          if (withSideNavTabs && sideNav?.tabs) {
            setTabs(sideNav.tabs.filter(Boolean));
          }

          setIsFetched(true);
        } catch (error) {
          captureException(error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadNavigationData();
  }, [isOpen, isShopInShop, withSideNavTabs, locale]);

  const fetchedNavigation = {
    ...navigation,
    children: sortNavigation(navigation?.children, type),
  };

  return { fetchedNavigation, tabs, isLoading };
};
